import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CheckList,
  DatePicker,
  DatePickerRef,
  Dialog,
  Empty,
  Form,
  Grid,
  Input,
  List,
  Modal,
  Picker,
  PickerRef,
  Popup,
  SearchBar,
  Selector,
  Space,
  Steps,
  TextArea,
  Toast,
} from 'antd-mobile';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import {
  getStaffOrgMapping,
  getDelayEnergyConsumptionDetail,
  GetDelayEnergyConsumptionDetailPostResponse,
  EnergyConsumptionAdminList,
  getEnergyConsumptionAdminList,
  GetAreaListPostResponse,
  getAreaList,
  DetailListVO,
  updateDelayEnergyConsumption,
  addDelayEnergyConsumption,
} from '@/api/energy';
import { isEmpty } from 'lodash-es';
import { useNavigate, useParams } from 'react-router-dom';
import { CloseCircleFill } from 'antd-mobile-icons';
import NoData from '@/assets/images/noData.png';
import SelectArea from './SelectArea';

const EnergyUseApplicationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [orgInfo, setOrgInfo] = useState<{ id: string; name: string }>();
  const [areaOp, setAreaOp] = useState<GetAreaListPostResponse[]>([]);
  const [detailList, setDetailList] = useState<DetailListVO[]>([{}]);
  const [mediumList, setMediumList] = useState<EnergyConsumptionAdminList[]>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);

  const [form] = Form.useForm();

  useEffect(() => {
    document.title = '延时申请';
  }, []);

  useEffect(() => {
    getStaffOrgMapping().then(res => {
      if (res) {
        setOrgInfo({ id: res.ownerOrgId!, name: res.ownerOrgName! });
      } else {
        setDisabled(true);
        Toast.show({ content: '当前申请人无权限申请' });
      }
    });
  }, []);

  useEffect(() => {
    if (orgInfo?.id) {
      getAreaList(orgInfo?.id).then(res => {
        setAreaOp(res.list);
      });
    } else {
      setAreaOp([]);
    }
  }, [orgInfo]);

  useEffect(() => {
    getEnergyConsumptionAdminList().then(res => {
      setMediumList(res.list ?? []);
    });
  }, []);

  useEffect(() => {
    getDelayEnergyConsumptionDetail(id!).then(res => {
      if (res) {
        const detailListData = (res.delayEnergyConsumptionDetailList || []).map(i => ({
          areaIdList: i.areaList?.map(a => a.id) ?? [],
          duration: i.duration,
          mediumIdList: i.mediumList?.map(m => m.id),
          workers: i.workers,
          content: i.content,
          startTime: i.startTime ? new Date(i.startTime) : undefined,
          endTime: i.endTime ? new Date(i.endTime) : undefined,
        }));
        form.setFieldsValue({
          delayEnergyConsumptionDetailList: detailListData,
        });
        setDetailList(detailListData);
      }
    });
  }, []);

  // 添加明细
  const addDetail = () => {
    const newDetailList = [...detailList, {}];
    setDetailList(newDetailList);
    form.setFieldsValue({
      delayEnergyConsumptionDetailList: newDetailList,
    });
  };

  // 删除明细
  const removeDetail = (index: number) => {
    const newDetailList = [...detailList];
    newDetailList.splice(index, 1);
    setDetailList(newDetailList);
    form.setFieldsValue({
      delayEnergyConsumptionDetailList: newDetailList,
    });
  };

  // 复制明细
  const copyDetail = (index: number) => {
    const detailToCopy = { ...detailList[index] };

    const newDetailList = [...detailList, detailToCopy];
    setDetailList(newDetailList);
    form.setFieldsValue({
      delayEnergyConsumptionDetailList: newDetailList,
    });
  };

  // 时间变化时计算时长
  const onTimeChange = (index: number, start?: Date, end?: Date) => {
    const { startTime, endTime } = form.getFieldValue(['delayEnergyConsumptionDetailList', index]);
    if (startTime && endTime) {
      // 计算时长（小时），保留两位小数
      const duration = parseFloat(
        (((end || endTime).valueOf() - (start || startTime).valueOf()) / (1000 * 60 * 60)).toFixed(2)
      );

      const newDetailList: any = [...detailList];
      newDetailList[index] = {
        ...newDetailList[index],
        startTime: start || startTime,
        endTime: end || endTime,
        duration,
      };

      setDetailList(newDetailList);
      form.setFieldsValue({
        delayEnergyConsumptionDetailList: newDetailList,
      });
    }
  };

  // 当表单项发生变化时同步更新detailList状态
  const onValuesChange = (changedValues: any, allValues: any) => {
    const fullValues = form.getFieldsValue(true);
    // 如果变化的是明细列表，则更新detailList状态
    if (allValues.delayEnergyConsumptionDetailList !== undefined) {
      setDetailList(fullValues.delayEnergyConsumptionDetailList);
    }
  };

  const onFinish = (isSave: boolean) => {
    form.validateFields().then(() => {
      const values = form.getFieldsValue(true);

      const params = {
        delayEnergyConsumptionDetailList: values.delayEnergyConsumptionDetailList.map((i: any) => ({
          ...i,
          startTime: i.startTime?.valueOf(),
          endTime: i.endTime?.valueOf(),
        })),
        ownerOrgId: orgInfo?.id,
        ownerOrgName: orgInfo?.name,
        type: isSave ? 1 : 0,
        id,
      };
      const api = id ? updateDelayEnergyConsumption : addDelayEnergyConsumption;
      api(params)
        .onError(error => {
          const { errorMessage } = error?.cause;
          Modal.alert({
            header: (
              <CloseCircleFill
                style={{
                  fontSize: 64,
                  color: 'var(--adm-color-danger)',
                }}
              />
            ),
            title: '用能计划冲突',
            content: (
              <Space direction="vertical">
                {errorMessage.split('\n').map((line: string, index: number) => (
                  <div key={index}>{line}</div>
                ))}
              </Space>
            ),
          });
          throw error;
        })
        .then(() => {
          navigate('/intelligent-platform/energyUse/application');
        });
    });
  };

  return (
    <div className={styles.page}>
      <List header="基本信息" className={styles.list}>
        <Form.Item label="申请部门" layout="horizontal" required>
          <Input placeholder="请输入" disabled value={orgInfo?.name} />
        </Form.Item>
      </List>
      <Form layout="horizontal" form={form} onValuesChange={onValuesChange} className={styles.detail}>
        {!isEmpty(detailList) && (
          <List header="申请明细" mode="card" className={styles.list}>
            {detailList!.map((item, index) => {
              return (
                <div key={index}>
                  <div className={styles.listTitleContainer}>
                    <span className={styles.listTitle}>明细{index + 1}</span>
                    <Button size="small" color="primary" fill="outline" onClick={() => copyDetail(index)}>
                      复制
                    </Button>

                    <Button size="small" color="primary" fill="none" onClick={() => removeDetail(index)}>
                      删除
                    </Button>
                  </div>
                  <Form.Item
                    label="区域选择"
                    name={['delayEnergyConsumptionDetailList', index, 'areaIdList']}
                    rules={[{ required: true, message: '请选择区域' }]}
                    onClick={() => setVisible(true)}
                  >
                    <SelectArea
                      onOk={params => {
                        const newDetailList: any = [...detailList];
                        newDetailList[index] = {
                          ...newDetailList[index],
                          areaIdList: params,
                        };

                        setDetailList(newDetailList);
                        form.setFieldsValue({
                          delayEnergyConsumptionDetailList: newDetailList,
                        });

                        setVisible(false);
                      }}
                      onCancel={() => setVisible(false)}
                      data={areaOp}
                      visible={visible}
                    />
                  </Form.Item>
                  <Form.Item
                    label="作业人数"
                    name={['delayEnergyConsumptionDetailList', index, 'workers']}
                    rules={[{ required: true, message: '请输入作业人数' }]}
                  >
                    <Input placeholder="请输入作业人数" type="number" min={1} max={999999} />
                  </Form.Item>
                  <Form.Item
                    label="作业内容"
                    layout="vertical"
                    name={['delayEnergyConsumptionDetailList', index, 'content']}
                    rules={[
                      { required: true, message: '请输入作业内容' },
                      { min: 1, max: 200, message: '请输入1-200个字符' },
                    ]}
                  >
                    <TextArea placeholder="请简要描述作业内容（200个字以内）" rows={1} maxLength={200} />
                  </Form.Item>
                  <Form.Item
                    label="用能类型"
                    layout="vertical"
                    name={['delayEnergyConsumptionDetailList', index, 'mediumIdList']}
                    rules={[{ required: true, message: '请选择用能类型' }]}
                  >
                    <Selector
                      columns={3}
                      options={(mediumList ?? []).map(item => ({
                        label: item.energyMediumName!,
                        value: item.energyMediumId!,
                      }))}
                      multiple
                    />
                  </Form.Item>
                  <Form.Item
                    name={['delayEnergyConsumptionDetailList', index, 'startTime']}
                    label="开始时间"
                    trigger="onConfirm"
                    onClick={(e, datePickerRef: RefObject<DatePickerRef>) => {
                      datePickerRef.current?.open();
                    }}
                  >
                    <DatePicker
                      precision="minute"
                      filter={{
                        minute: val => val === 0 || val === 30,
                      }}
                      onConfirm={value => {
                        onTimeChange(index, value, undefined);
                      }}
                    >
                      {value =>
                        value ? (
                          <span style={{ color: '#333' }}>{dayjs(value).format('YYYY-MM-DD HH:mm')}</span>
                        ) : (
                          '请选择日期'
                        )
                      }
                    </DatePicker>
                  </Form.Item>
                  <Form.Item
                    name={['delayEnergyConsumptionDetailList', index, 'endTime']}
                    label="结束时间"
                    trigger="onConfirm"
                    onClick={(e, datePickerRef: RefObject<DatePickerRef>) => {
                      datePickerRef.current?.open();
                    }}
                  >
                    <DatePicker
                      precision="minute"
                      filter={{
                        minute: val => val === 0 || val === 30,
                      }}
                      onConfirm={value => {
                        onTimeChange(index, undefined, value);
                      }}
                    >
                      {value =>
                        value ? (
                          <span style={{ color: '#333' }}>{dayjs(value).format('YYYY-MM-DD HH:mm')}</span>
                        ) : (
                          '请选择日期'
                        )
                      }
                    </DatePicker>
                  </Form.Item>
                  <Form.Item label="用能时长">
                    <span style={{ color: '#333' }}>{item.duration ? item.duration + '小时' : '--'}</span>
                  </Form.Item>
                </div>
              );
            })}
          </List>
        )}
        <Button color="primary" fill="outline" className={styles.add} onClick={addDetail} disabled={disabled}>
          添加明细
        </Button>
      </Form>
      <div className={styles.actionBar}>
        <Grid columns={3} gap={12}>
          <Button size="large" onClick={() => navigate('/intelligent-platform/energyUse/application')}>
            取消
          </Button>
          <Button size="large" disabled={disabled} onClick={() => onFinish(false)}>
            保存草稿
          </Button>
          <Button size="large" color="primary" disabled={disabled} onClick={() => onFinish(true)}>
            提交
          </Button>
        </Grid>
      </div>
    </div>
  );
};

export default EnergyUseApplicationForm;
